<script>
import { mapActions } from "vuex";
import TargetsTab from "./targets.vue";
import Documents from '../documents/Documents.vue';
import SwitchButton from "../../components/SwitchButton.vue";
import MixedIcons from "../../assets/icons/MixedIcons.vue";
import i18n from "@/plugins/i18n";
import NameBubble from "../../components/name-bubble.vue";
import MetricDiscussions from "./discussion/metric-discussions.vue";

import ApprovalButton from "../../components/Approval/approval-button.vue";
import ApprovalDashboard from "../../components/Approval/approval-dashboard.vue";
import { approval_options } from "../../utils/config";
import ApprovalButtonSimple from "../../components/Approval/approval-button-simple.vue";

import html2canvas from "html2canvas";
import jsPdf from "jspdf";

export default {
  props: {
    overrideCompanyId: {
      type: Number,
      default: null,
    },
  },

  components: {
    TargetsTab,
    Documents,
    SwitchButton,
    MixedIcons,
    NameBubble,
    MetricDiscussions,
    ApprovalButton,
    ApprovalButtonSimple,
    ApprovalDashboard,
  },

  data() {
    return {
      activeTab: "metrics",

      officers: [],
      selectedOfficerToFilterBy: null,

      inputsTree: [],
      inputsMap: {},
      currency: null,

      userAssignments: null,

      updateModal: false,
      selectedInputID: "",
      selectedInputType: "",

      loading: false,
      companies: [],
      facilities: [],
      selectedFacilityID: null,
      selectedCategoryID: null,

      kpiType: "",
      kpiInputId: null,

      isYearly: true,
      maxYear: 2024,

      auditorComment: {
        comment: null,
        status: null,
      },

      metricForDiscussion: null,
      metricForApproval: null,

      hideButtonsForExport: false,
    }
  },

  computed: {
    selectedCompanyId() {
      return this.overrideCompanyId || this.$user.company_id;
    },

    availableYears() {
      if (!this.maxYear)
        return [2024, 2023, 2022, 2021, 2020, 2019];

      const year = new Date().getFullYear();
      return [year, year - 1, year - 2, year - 3, year - 4, year - 5];
    },

    availableQuarters() {
      return [1, 2, 3, 4];
    },

    selectedYears() {
      return [this.maxYear - 2, this.maxYear - 1, this.maxYear];
    },

    switchButtonLabels() {
      const yearly = i18n.t("kpisMetrics.yearly_metrics");
      const quarterly = i18n.t("kpisMetrics.quarterly_metrics");
      return [yearly, quarterly];
    },

    hasSingleFacility() {
      return (this.facilities.length == 2); // !! facility length amounts to 2 because we push an additional company based facility!!!
    },

    carbonEndpoint() {
      return process.env.VUE_APP_CARBON_DOMAIN;
    },

    approvalOptionsMap() {
      let map = new Map();
      for (let i = 0; i < approval_options.length; i++) {
        const o = approval_options[i];
        map.set(o.key, o);
      }

      return map;
    },
  },

  async mounted() {
    await this.setMaxYear(new Date().getFullYear() - 1);
    await this.myFacilities();
    await this._getCompanyOfficers();
    await this.handleInitialRouteQuery();
  },

  methods: {
    ...mapActions('admin', [
      'getCompanyOfficers',
      'checkHasAssignment',
    ]),

    ...mapActions("KPIsMetrics", [
      'getInputAnalysis',
      'exportInputAnalysis',
      'getAssignments',
      'getFacilities',
      'getMetricById',
    ]),

    async _getCompanyOfficers() {
      await this.getCompanyOfficers()
        .then(res => {
          this.officers = res.data;
        })
        .catch(({ response }) => {
          this.$notify('error', response.data.messageName);
        })
        .finally(async () => {
          if (this.$user.role_id > 4) {
            await this._checkHasAssignment();
          }
        })
    },

    async _checkHasAssignment() {
      await this.checkHasAssignment()
        .then(async (res) => {
          if (res.data.value == true) {
            const me = this.officers.find(x => x.user_id === this.$user.user_id);
            if (me)
              await this.selectOfficerToFilterBy(null, me);
          }
        })
        .catch(({ response }) => {
          console.error(response.data.messageName);
        })
    },

    async _getInputAnalysis() {
      await this.setLoading(true);
      const params = {
        company_id: this.selectedCompanyId,
        facility_id: this.selectedFacilityID,
        is_yearly: this.isYearly,
        year: this.maxYear,
      };

      await this.getInputAnalysis(params)
        .then(res => {
          this.inputsTree = res.data.tree;
          this.inputsMap = res.data.maps;
          this.currency = res.data.currency;
        })
        .catch(({ response }) => {
          this.$notify('error', response.data.messageName);
        })
        .finally(async () => {
          await this._getAssignments(params.facility_id);
          await this.setLoading(false);
        })
    },

    async _exportInputAnalysis() {
      if (this.$user.role_id > 4) 
        return;

      await this.setLoading(true);
      const params = {
        company_id: this.selectedCompanyId,
        facility_id: this.selectedFacilityID,
        is_yearly: this.isYearly,
        year: this.maxYear,
      };

      await this.exportInputAnalysis(params)
        .then(res => {
          const url = URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            'download',
            'KPI_Monitoring.xlsx'
          );
          document.body.appendChild(link);
          link.click();
        })
        .catch(({ response }) => {
          this.$notify('error', response.data.messageName);
        })
        .finally(async () => {
          await this.setLoading(false);
        })
    },

    async _getAssignments(facility_id) {
      await this.getAssignments(facility_id)
        .then(res => {
          this.userAssignments = res.data.value;
        })
    },

    async myFacilities() {
      const params = {
        company_id: this.selectedCompanyId,
        is_deleted: false
      };
      await this.getFacilities(params)
        .then(res => {
          this.facilities = res.data;
          this.facilities.push(
            {
              facility_name: i18n.t('kpisMetrics.company_based'),
              facility_id: 0
            }
          );

          const length = this.facilities.length;
          if (this.facilities.length == 2) { // !! facility length amounts to 2 because we push an additional company based facility!!!
            this.selectedFacilityID = this.facilities[0].facility_id;
          } else if (this.facilities.length > 1) {
            this.selectedFacilityID = this.facilities[length - 1].facility_id;
          }
        })
        .finally(async () => {
          await this._getInputAnalysis()
        })
    },

    async onFacilityChange() {
      this.activeTab = "metrics";
      await this._getInputAnalysis();
    },

    async handleInitialRouteQuery() {
      const query = this.$route.query;
      if (query['discussion'] != null && query['discussion'] != undefined) {
        const metric = await this.getMetricById(query['discussion']);
        if (!metric) return;
        await this.openDiscussion(metric);
      }
    },

    // setters //

    async setMaxYear(year) {
      this.maxYear = year;
    },

    async setLoading(bool) {
      this.loading = bool;
    },

    async setButtonState(state) {
      this.isYearly = state;
    },

    // utils //

    async checkButtonState(state) {
      await this.setButtonState(state);
      await this._getInputAnalysis();
    },

    async changeMaxYear(direction) {
      try {
        const yearsRange = this.availableYears.length;
        if (direction == "left" && (this.maxYear - 2 != this.availableYears[yearsRange - 1])) {
          const newMaxYear = this.maxYear - 1;
          await this.setMaxYear(newMaxYear);
          await this._getInputAnalysis();
        } else if (direction == "right" && this.maxYear != this.availableYears[0]) {
          const newMaxYear = this.maxYear + 1;
          await this.setMaxYear(newMaxYear);
          await this._getInputAnalysis();
        } else if (direction != "right" && direction != "left") {
          throw new Error("Invalid direction on changeMaxYear!");
        }
      } catch (error) {
        console.error(error.message);
        throw new Error(error);
      }
    },

    selectOfficerToFilterBy(_, officer) {
      if (this.selectedOfficerToFilterBy != null && this.selectedOfficerToFilterBy.user_id === officer.user_id) {
        this.selectedOfficerToFilterBy = null;
      } else {
        this.selectedOfficerToFilterBy = officer;
      }
    },

    changeTab(tab) {
      this.activeTab = tab;
    },

    goToKpi(category, element, metric) {
      const url = this.$router.resolve({
        path: '/kpi-metrics',
        query: {
          category: category.sust_category_id,
          element: element.sust_element_id,
          target: metric.sust_target_id,
          kpi: metric.sust_kpi_id,
          metric: metric.sust_metric_kpi_id,
        }
      }).href;

      window.open(url, '_blank');
    },

    openDocumentsModal(input, type) {
      if (!input || input.value === '-') {
        console.warn('No inputs found for the specified year and quarter: ', input.year, ' ', input.quarter);
        return;
      }
      
      this.kpiType = type;
      this.kpiInputId = input.metrics_input_table_id;
      this.$refs.documentsPopup.openPopup();
    },

    /* scrollToView(ref) { // might use this later
      if (ref == "category_1") {
        this.selectedCategoryID = 1;
      } else if (ref == "category_2") {
        this.selectedCategoryID = 2;
      } else if (ref == "category_3") {
        this.selectedCategoryID = 3;
      } else if (ref == "category_4") {
        this.selectedCategoryID = 4;
      } else return

      const target = document.getElementById(ref);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "end"
        })
      }
    }, */

    getUnitType(unit) {
      if (unit === 'null' || unit === null) {
        return '-';
      } else if (unit === 'currency') {
        return this.currency;
      } else {
        return unit;
      }
    },

    getMetricInput(metric, year, quarter) {
      const non_existent_template = {
        metrics_input_table_id: null,
        value: '-',
        year: year,
        quarter: quarter,
        approval_status: {
          status: 'pending',
          last_edited_by: '-',
          last_edited_on: '-',
        },
        has_document: false
      };

      if (!metric || !metric.values || !year || !quarter)
        return non_existent_template;

      if (metric.sust_metric_kpi_id != undefined && metric.sust_sub_metric_kpi_id == undefined) { // if is metric
        const key = `M=${metric.sust_metric_kpi_id}&S=none&Y=${year}&Q=${quarter}`;
        const value = this.inputsMap['metric_inputs_map'][key]

        if (!value) return non_existent_template;
        return value;

      } else if (metric.sust_metric_kpi_id == undefined && metric.sust_sub_metric_kpi_id != undefined) { // if is submetric
        const key = `M=none&S=${metric.sust_sub_metric_kpi_id}&Y=${year}&Q=${quarter}`;
        const value = this.inputsMap['sub_metric_inputs_map'][key]

        if (!value) return non_existent_template;
        return value;

      } else return non_existent_template;
    },

    checkHasDocument(metric, year, quarter) {
      if (!metric || !metric.values || !year || !quarter)
        return false;

      const input = metric.values.find(value => value.year == year && value.quarter === quarter);
      if (input) {
        return input.has_document ? 'fa-solid fa-check' : null;
      } else {
        return false;
      }
    },

    filteredMetrics(element) {
      let metrics = element['sust_metric_kpis'] ? element['sust_metric_kpis'] : [];
      
      if (this.selectedOfficerToFilterBy != null)
        metrics = metrics.filter(x => x.metric_assignments.some(y => y.assigned_users.some(z => z.user_id == this.selectedOfficerToFilterBy.user_id)));

      return metrics;
    },

    //#region AUDITOR

    clickOpenAuditorComments(val) {
      this.auditorComment = {
        comment: val.auditor_comments,
        status: val.metric_audit_status,
      }
      this.$refs.auditorComments.openPopup();
    },

    resetAuditorComments() {
      this.auditorComment = {
        comment: null,
        status: null,
      }
    },

    //#endregion AUDITOR

    //#region DISCUSSION 

    async openDiscussion(metric) {
      await this.setMetricForDiscussion(metric);
      
      const { discussion } = this.$route.query;
      if (discussion == null || discussion == undefined) {
        this.$router.push(
          {
            query: {
              ...this.$route.query,
              discussion: metric.sust_metric_kpi_id,
            }
          }
        );
      }

      this.$refs.metricDiscussion.openPopup();
    },

    async setMetricForDiscussion(metric) {
      this.metricForDiscussion = metric;
    },

    closeDiscussion() {
      this.$refs.metricDiscussion.startHideProcess();
    },

    //#endregion DISCUSSION


    //#region APPROVAL

    getCanAudit(metric) {
      if (this.userAssignments === null) return false;
      if (this.userAssignments === 'all') return true;

      const idx = this.userAssignments.audit.findIndex(x => x.sust_metric_kpi_id === metric.sust_metric_kpi_id);
      return idx != -1;
    },
    
    openApprovalPopup(metric, input) {
      if (!input) {
        this.metricForApproval = null;
        this.$refs.approvalPopup.startHideProcess();
      } else {
        const can_audit = this.getCanAudit(metric);
        if (!can_audit) return;

        const type = metric.sust_sub_metric_kpis === null ? 'metric' : 'sub_metric';
        this.metricForApproval = {
          type: type,
          id: metric[`sust_${type}_kpi_id`],
          name: metric[`sust_${type}_kpi_name`],
          company_id: this.selectedCompanyId,
          facility_id: this.selectedFacilityID,
          ...input
        };
        this.$refs.approvalPopup.openPopup();
      }
    },

    handleApprovalUpdate(input) {
      const key = input.type === 'metric' ?
        `M=${input.id}&S=none&Y=${input.year}&Q=${input.quarter}` :
        `M=none&S=${input.id}&Y=${input.year}&Q=${input.quarter}`;
      
        this.inputsMap[`${input.type}_inputs_map`][key]['approval_status'] = input.approval_status;
    },

    //#endregion APPROVAL

    async setHideButtons(bool) {
      this.hideButtonsForExport = bool;
    },

    async exportAsPdf() {
      await this.setHideButtons(true);
      const element = this.$refs.kpiMonitoringContainer;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPdf("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("kpi-monitoring.pdf");

      await this.setHideButtons(false);
    }
  },
}
</script>

<template>
  <div style="width: 100%;">
    <div class="flex-all-centered-col" style="justify-content: flex-start;">
      <loading-spinner v-if="loading" :container="true" :color="'var(--sustainfinity-green)'" :text="$t('misc.wait_fetching_data')" :fontSize="'2vw'" />

      <div v-if="!loading" class="top-select-inputs">
        <select v-if="activeTab === 'metrics' && !hasSingleFacility" class="monitor-page-facility-select"
          style="max-height: 35px; align-self: flex-start;" v-model="selectedFacilityID" @change="onFacilityChange">
          <option v-for="(facility, index) in facilities" :key="index" :value="facility.facility_id">
            {{ facility.facility_name }}
          </option>
        </select>

        <switch-button class="switch-button" @buttonState="checkButtonState($event)" :labelOn="switchButtonLabels[0]"
          :labelOff="switchButtonLabels[1]" :colorOn="'var(--sustainfinity-navy)'"
          :colorOff="'var(--sustainfinity-blue)'" :thumbColor="'whitesmoke'" :initialValue="isYearly" 
        />

        <div class="year-selector" v-if="isYearly">
          <i class="fa-solid fa-caret-left" @click="changeMaxYear('left')"></i>
          {{ maxYear - 2 }} - {{ maxYear }}
          <i class="fa-solid fa-caret-right" @click="changeMaxYear('right')"></i>
        </div>

        <div class="year-selector" v-else>
          <i class="fa-solid fa-caret-left" @click="changeMaxYear('left')"></i>
          {{ maxYear }}
          <i class="fa-solid fa-caret-right" @click="changeMaxYear('right')"></i>
        </div>

        <button class="sust-btn sust-btn-blue" @click="_exportInputAnalysis()"
          style="margin-left: 1em;"
        >
          {{ $t('kpisMetrics.misc.export_xls') }}
        </button>

        <button class="sust-btn sust-btn-blue" @click="exportAsPdf()"
          style="margin-left: 1em;"
        >
          {{ $t('kpisMetrics.misc.export_pdf') }}
        </button>
      </div>

      <div v-if="officers.length > 0" class="officer-filters-ctr">
        <name-bubble v-for="(officer, idx) in officers" :Words="[officer.firstname, officer.surname]" 
          :key="idx"
          :XonHover="false"
          :Radius="30"
          :PutEmphasis="selectedOfficerToFilterBy == null ? true : (officer.user_id == selectedOfficerToFilterBy.user_id)"
          :style="{transform: `translateX(-${idx * 10}%)`}" 
          @clickBubble="selectOfficerToFilterBy($event, officer)"
          v-sust-tooltip="{ text: `${officer.firstname} ${officer.surname}`, bgColor: 'var(--sustainfinity-navy)' }" 
        />
      </div>

      <div v-if="activeTab === 'metrics' && inputsTree.length > 0 && !loading" 
        class="flex-all-centered-col"
        ref="kpiMonitoringContainer"
      >
        <div class="category-section" v-for="(category, index) in inputsTree" :key="index">
          <div v-if="category['sust_elements'] && category['sust_elements'].length > 0" class="category-name"
            :style="{ 'borderBottomColor': category.color }" :id="`category_${category.sust_category_id}`">
            <div class="category-name--border" :style="{ 'borderBottomColor': category.color }">
              {{ category['sust_category_name'] }}
            </div>
          </div>

          <div class="category-card" v-for="(element, elementIndex) in category['sust_elements']" :key="elementIndex">
            <div class="element-name" :style="{ 'color': category.color }">
              <span>
              {{ element.sust_element_name }}
              </span>
              <div v-if="element.sust_element_id == 7 && !hideButtonsForExport">
                <div class="carbon-logo-container">
                  <a class="carbon-link" :href="carbonEndpoint" target="_blank">
                    <img src="/images/SustAppLogos/Sustainfinity-Carbon-Logo.png" alt="Carbon Logo">
                  </a>
                </div>
              </div>
            </div>


            <div class="headers">
              <div :style="{ 'backgroundColor': category.color }" class="header-flex">
                {{ $t('kpisMetrics.metrics').toUpperCase() }}
              </div>

              <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period"
                :style="{ 'backgroundColor': category.color }" class="value-flex"
              >
                <div class="value-cell-value">
                  {{ isYearly ? period : `Q${period}` }}
                </div>
                <div class="value-cell-buttons">

                </div>
              </div>
            </div>

            <div v-for="(metric, metricIndex) in filteredMetrics(element)" :key="metricIndex">
              <div class="values">
                <!-- metric name -->
                <div :style="{ 'borderBottom': '.13em solid' + category.color }" class="header-flex">
                  <a @click="goToKpi(category, element, metric)">
                    {{ metric.sust_metric_kpi_name }} ( {{ getUnitType(metric.unit_type) }} )
                  </a>
                  <button v-if="!hideButtonsForExport" class="icon-button" v-sust-tooltip="{text: $t('metric_discussions.open_discussion'), fontSize: '.5em'}"
                    @click="openDiscussion(metric)"
                  >
                    <i class="fa-solid fa-comments"></i>
                  </button>
                </div>

                <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period"
                  :style="{ 'borderBottom': '.13em solid' + category.color }" class="value-flex"
                > 
                  <div class="value-cell-value">
                    {{ getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period).value }}
                  </div>

                  <div v-if="!hideButtonsForExport && metric.sust_sub_metric_kpis === null" class="value-cell-buttons">
                    <button v-if="getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period).value != '-'"
                      class="icon-button"
                      :disabled="!getCanAudit(metric)"
                      @click="openApprovalPopup(metric, getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period))"
                    >
                      <approval-button-simple 
                        :approval_status="getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period).approval_status"
                      />
                    </button>

                    <mixed-icons
                      :disabled="getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period).value === '-'"
                      :name="'fa-regular fa-folder-open'"
                      :hover="true"
                      :side_class="checkHasDocument(metric, isYearly ? period : maxYear, isYearly ? 5 : period)"
                      :side_color="'var(--sustainfinity-green)'"
                      :main_color="category.color"
                      @click="openDocumentsModal(getMetricInput(metric, isYearly ? period : maxYear, isYearly ? 5 : period), 'metric')"
                      class="documentation-icon" 
                    />
                  </div>

                </div>
              </div>

              <div v-for="(submetric, submetricIndex) in metric['sust_sub_metric_kpis'] ? metric['sust_sub_metric_kpis'] : []" :key="submetricIndex"
                class="sub-values"  
              >
                <!-- sub metric name -->
                <div :style="{ 'borderBottom': '.15em dashed' + category.color }" class="header-flex" style="justify-content: center;">
                  {{ submetric.sust_sub_metric_kpi_name }} ( {{ getUnitType(submetric.unit_type) }} )
                </div>

                <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period"
                  :style="{ 'borderBottom': '.15em dashed' + category.color }" class="value-flex"
                >
                  <div class="value-cell-value">
                    {{ getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period).value }}
                  </div>
                  
                  <div v-if="!hideButtonsForExport" class="value-cell-buttons">
                    <button v-if="getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period).value != '-'"
                      class="icon-button"
                      :disabled="!getCanAudit(submetric)"
                      @click="openApprovalPopup(submetric, getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period))"
                    >
                      <approval-button-simple 
                        :approval_status="getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period).approval_status"
                      />
                    </button>

                    <mixed-icons
                      :disabled="getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period).value === '-'"
                      :name="'fa-regular fa-folder-open'"
                      :hover="true"
                      :side_class="checkHasDocument(submetric, isYearly ? period : maxYear, isYearly ? 5 : period)"
                      :side_color="'var(--sustainfinity-green)'" 
                      :main_color="category.color"
                      @click="openDocumentsModal(getMetricInput(submetric, isYearly ? period : maxYear, isYearly ? 5 : period), 'sub_metric')"
                      class="documentation-icon" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!inputsTree || inputsTree.length == 0 || inputsTree == undefined"
        style="text-align: center;"
      >
        <br><br><br>
        <div style="font-weight: bold; font-size: 1.5vw;">
          {{ $t('kpisMetrics.no_values') }}
        </div>
        <div style="font-weight: bold; font-size: 1.2vw;">
          {{ $t('kpisMetrics.switch_for_value') }}
        </div>
      </div>

      <targets-tab v-if="activeTab === 'targets'" />
    </div>

    <sust-popup ref="documentsPopup" :footer="false" :header="false" size="xl">
      <div style="width: 100%; min-height: fit-content;">
        <Documents :Category="kpiType" :InputId="kpiInputId" />
      </div>
    </sust-popup>

    <sust-popup ref="auditorComments" size="md"
      :title="`${$t('auditor.status._status').toUpperCase()}: `.concat($t(`auditor.status.${auditorComment.status}`).toUpperCase())"
      :footer="false" @onHide="resetAuditorComments()">
      <div class="auditor-comments">
        {{ auditorComment.comment }}
      </div>
    </sust-popup>

    <sust-popup
      ref="metricDiscussion" size="md"
      :header="false"
      :footer="false"
      :custom-style="{'background-color': 'var(--carbon)', 'color': 'whitesmoke'}"
      :body-custom-style="{'padding': 0}"
      @onHide="() => $router.replace({ query: '' })"
    >
      <metric-discussions
        :metric="metricForDiscussion"
        @close="closeDiscussion()"
      />
    </sust-popup>

    <sust-popup
      ref="approvalPopup" size="xl"
      :title="metricForApproval ? metricForApproval.sust_metric_kpi_name : '-'"
      :footer="false"
    >
      <approval-dashboard v-if="metricForApproval != null"
        :input="metricForApproval"
        @update_input="handleApprovalUpdate($event)"
      />
    </sust-popup>
  </div>
</template>

<style scoped lang="scss">

.category-section {
  width: 100%;
  margin-bottom: 2em;
}

.category-card {
  position: relative;
}

.category-name {
  font-size: 1.66rem;
  font-weight: bold;
  color: #023047;

  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.category-name--border {
  border-bottom: medium solid;
  max-width: fit-content;
  margin-right: 2.5%;
}

.category-card {
  filter: drop-shadow(0 .25em .33em gray) !important;
  border: thin solid lightgray;
  border-radius: 1em;

  margin-top: 1em;
  margin-right: 2em;

  background-color: #f5f5f5;

  padding: 1em;
}

.element-name {
  font-weight: bold;
  font-size: 1rem;

  padding-bottom: 1em;
  padding-left: .5em;
}

.header-flex {
  flex: 3;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  > a {
    cursor: pointer;
    &:hover {
      text-decoration: underline !important;
    }
  }
}

.value-flex {
  flex: 1;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  > .value-cell-value {
    flex: 9;
    text-align: center;
  }
  
  > .value-cell-buttons {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
}

.headers {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  margin-left: .33em;
  font-size: .9rem;
}

.headers>div {
  border-top-left-radius: .33em;
  border-top-right-radius: .33em;

  margin-right: .33em;
  padding: .1em;

  color: white;
  font-weight: bolder;

  filter: drop-shadow(0 0 .33em lightgray);
}

.values {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;

  margin-left: .33em;
  font-size: .8rem;
}

.values>div {
  margin-right: .33em;
  padding: .1em;

  color: #023047;
  font-weight: bold;

  background-color: #f3f3f3;

  filter: drop-shadow(0 0 .33em lightgray);
}

.sub-values {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;

  margin-left: .33em;
  font-size: .8rem;
  text-align: center;
}

.sub-values>div {
  margin-right: .33em;
  padding: .1em;

  color: #023047;
  font-weight: bold;

  background-color: #f3f3f3;

  border-bottom: thin dashed lightgray;

  filter: drop-shadow(0 0 .33em lightgray);
}

.sub-years--value {
  width: 18%;

  text-align: center;
}

.tabs-bar {
  margin-top: 1em;

  &>.tab {
    background-color: var(--sustainfinity-navy);
    color: white;
    font-weight: bold;
    padding: .33em;
    cursor: pointer;

  }

  &>.report-sections-bar {
    border: .2em solid var(--sustainfinity-navy);
    border-top-left-radius: 1em;
    border-bottom-left-radius: 1em;
  }

  &>.section-texts-bar {
    border: .2em solid var(--sustainfinity-navy);
    border-top-right-radius: 1em;
    border-bottom-right-radius: 1em;
  }

  &>.active-tab {
    background-color: white !important;
    color: var(--sustainfinity-navy) !important;
  }
}

.go-to-kpi {
  font-size: 1.5em;
  padding: 0 1em 0 1em;

  transition: all 100ms;
  cursor: pointer;

  &:hover {
    filter: brightness(120%);
  }
}

.top-select-inputs {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  align-items: center;
  width: 100%;
  padding-bottom: .5em;
  margin-bottom: .5em;

  >.monitor-page-facility-select {
    border: 0;
    border-bottom: thin solid var(--sustainfinity-navy);
    outline: 0;
    background: 0;
    padding: .33em .66em .33em .66em;

    cursor: pointer;

    &:focus {
      outline: thin solid var(--sustainfinity-navy);
    }
  }

  >.year-selector {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-content: center;
    align-items: center;
    margin-left: 3em;

    >i {
      transform: translateY(5%);
      cursor: pointer;
      transition: all 100ms;

      &:hover {
        color: darkgray;
      }
    }

    >.fa-caret-left {
      padding-right: .5em;
    }

    >.fa-caret-right {
      padding-left: .5em;
    }
  }
}

.documentation-icon {
  font-size: .5em;
  /* scale: .6; */
}

.switch-button {
  margin-left: 3em;
}

.is-quarterly-cell {
  width: 14% !important;
}

.carbon-logo-container {
  position: absolute;
  transform: translateY(-77%);
  right: 5%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 15px;
  background-color: whitesmoke;
  border: thin solid var(--sustainfinity-green);

  width: 20%;
  min-height: 40px;
  max-width: 110px;
  max-height: 50px;
  border-radius: 7px;

  cursor: pointer;

  &:hover {
    background: white;
    box-shadow: 0 0 .6em .1em white;
    filter: drop-shadow(0 .2em .1 em var(--sustainfinity-green));
    scale: 1.025;
  }

  .carbon-link img {
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    width: 80%;
    height: auto;
    max-width: 120px;
    max-height: 140px;
    object-fit: contain;
  }
}

.auditor-comments-ctr {
  position: absolute;
  left: 1em;
  top: 50%;
  transform: translateY(-50%);

  transition: all 150ms;
  cursor: pointer;

  &:hover {
    filter: brightness(120%);
  }
}

.auditor-status-ctr {
  font-size: 1.1em;
  position: absolute;
  left: 2.5em;
  top: 50%;
  transform: translateY(-55%);
}

.auditor-document-status-ctr {
  font-size: 1.1em;
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
}

.officer-filters-ctr {
  border-bottom: thin solid lightgray;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding-bottom: .5em;
}

.approval-button {
  position: absolute;
  left: 0;
}

.icon-button:disabled {
  background-color: transparent !important;
  pointer-events: all !important;
  cursor: default !important;
}
</style>